import { api } from './api';
import type {
  LoginRequest,
  RegisterRequest,
  TokenResponse,
  User,
} from '@/types';

// Бэкенд проксирует ответ Keycloak как есть — а Keycloak отдаёт токены в
// snake_case (access_token, refresh_token, expires_in, token_type). Фронтенд
// же работает в camelCase (TokenResponse). Без этой нормализации tokens.accessToken
// оказывался undefined → interceptor не ставил Authorization → /me редиректило
// в OAuth-флоу (302) → "Network error" при входе.
// Толерантны к обоим форматам: если бэкенд когда-то начнёт отдавать camelCase,
// ничего не сломается.
type RawTokenResponse = Partial<{
  access_token: string;
  accessToken: string;
  refresh_token: string;
  refreshToken: string;
  expires_in: number;
  expiresIn: number;
  token_type: string;
  tokenType: string;
}>;

export function normalizeTokens(raw: RawTokenResponse | null | undefined): TokenResponse {
  return {
    accessToken: raw?.accessToken ?? raw?.access_token ?? '',
    refreshToken: raw?.refreshToken ?? raw?.refresh_token ?? '',
    expiresIn: raw?.expiresIn ?? raw?.expires_in ?? 0,
    tokenType: raw?.tokenType ?? raw?.token_type ?? 'Bearer',
  };
}

export const authService = {
  async login(credentials: LoginRequest): Promise<TokenResponse> {
    const response = await api.auth.post<RawTokenResponse>('/login', credentials);
    return normalizeTokens(response.data);
  },

  async register(data: RegisterRequest): Promise<string> {
    // Бэкендный DTO RegisterRequest принимает только email + password.
    // confirmPassword нужно клиенту (валидация совпадения), но не бэкенду —
    // отправляем ровно те поля, что описаны в контракте.
    const response = await api.auth.post('/register', {
      email: data.email,
      password: data.password,
    });
    return response.data;
  },

  async confirmEmail(token: string): Promise<string> {
    const response = await api.auth.get('/confirm', { params: { token } });
    return response.data;
  },

  async refreshToken(refreshToken: string): Promise<TokenResponse> {
    const response = await api.auth.post<RawTokenResponse>('/refresh', { refreshToken });
    return normalizeTokens(response.data);
  },

  async logout(refreshToken: string): Promise<void> {
    await api.auth.post('/logout', { refreshToken });
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.auth.get<User>('/me');
    return response.data;
  },

  /**
   * Отметить oauth-email как «спрошенный» (требование 3): фронт вызывает после
   * того, как пользователь ответил Да/Нет на вопрос об использовании email
   * из OAuth для канала уведомлений. Бэкенд больше не предлагает экран.
   */
  async markOauthEmailConsumed(): Promise<void> {
    await api.auth.post('/me/oauth-email-consumed');
  },

  async getOAuthSession(): Promise<TokenResponse> {
    // Вторая половина OAuth-флоу: после колбэка бэкенд положил токены в
    // httpOnly cookie (SameSite=Lax). SPA дёргает этот эндпоинт сразу при
    // загрузке страницы /auth/callback: бэкенд читает cookie и отдаёт токены
    // в JSON (как при обычном логине). withCredentials обязателен — без него
    // браузер не пришлёт cookie на XHR-запрос. Точечно для этого запроса,
    // чтобы не менять поведение остальных клиентов.
    const response = await api.auth.get<RawTokenResponse>('/login/oauth-session', {
      withCredentials: true,
    });
    return normalizeTokens(response.data);
  },

  async socialLogin(provider: string): Promise<void> {
    // Полный top-level переход браузера на бэкенд-эндпоинт OAuth-флоу.
    // ВАЖНО: путь строим как ОТНОСИТЕЛЬНЫЙ (/api/auth/login/social), чтобы браузер
    // всегда ходил через свой же origin → через тот же reverse-proxy (vite dev-proxy
    // в dev / nginx в Docker), который маршрутизирует /api/auth на auth-сервис.
    // Раньше путь собирался из axios baseURL, который в dev равен http://localhost:8000
    // (без префикса /api/auth), из-за чего запрос уходил на /login/social мимо контроллера
    // (маппинг /api/auth/**) → 404. Относительный путь устойчив к любому окружению.
    window.location.href = `/api/auth/login/social?provider=${encodeURIComponent(provider)}`;
  },

  async linkAccount(provider: string): Promise<void> {
    window.location.href = `/api/auth/link?provider=${encodeURIComponent(provider)}`;
  },

  async forgotPassword(email: string): Promise<string> {
    const response = await api.auth.post('/forgot-password', { email });
    return response.data;
  },

  async resetPassword(token: string, password: string): Promise<string> {
    const response = await api.auth.post('/reset-password', { token, password });
    return response.data;
  },
};
